import DataBin2D from "../../valueObjects/DataBin2D.js";
import DataBinning from "./DataBinning.js";

class DataBinning2D {
    /**
     * Creates instance of two dimensional data binning tool
     * @param {DataBinning} dataBinningX - data binning instance for X axis
     * @param {DataBinning} dataBinningY - data binning instance for Y axis
     * @returns {DataBinning2D} DataBinning2D instance
     *
     */
    constructor(dataBinningX, dataBinningY) {
        if (!(dataBinningX instanceof DataBinning)) {
            throw new TypeError('dataBinningX must be instance of DataBin');
        }

        if (!(dataBinningY instanceof DataBinning)) {
            throw new TypeError('dataBinningY must be instance of DataBin');
        }

        this.dataBinningX = dataBinningX;
        this.dataBinningY = dataBinningY;
    }

    /**
     * Simple data binning based on grid of size binSizeX x binSizeY
     * @param {Array} data array of 2 dimensional arrays, each array should contain 2 elements
     * - first (index 0) represents X
     * - second (index 1) represents Y
     * @returns {Array} array of bins, each bin will contain x, y, sizeX and sizeY properties and set of data points which were classified to given bin
     */
    determineBins(data) {
        if (typeof(data) === 'undefined') {
            throw new TypeError('data must be defined');
        }

        if (!(data instanceof Array)) {
            throw new TypeError('data must be iterable');
        }

        if (!data.length) {
            return [];
        }

        return this.performBinning(data);
    }

    performBinning(data) {
        const traversed = data.reduce((prev, point) => {
            prev.x.push(point[0]);
            prev.y.push(point[1]);
            return prev;
        }, {x: [], y: []});
        
        const dataX = traversed.x;
        const dataY = traversed.y;

        const binsGrid = this.createGridOfBins(dataX, dataY);

        const minY = binsGrid[0][0].fromWhereY();
        const maxY = binsGrid[binsGrid.length - 1][0].toWhereY();
        const minX = binsGrid[0][0].fromWhereX();
        const maxX = binsGrid[0][binsGrid[0].length - 1].toWhereX();

        for (let i = 0; i < data.length; i++) {
            const point = data[i];

            if (!(point instanceof Array)) {
                throw new TypeError(`All items in data array must be of array type, error at index ${i}`);
            }

            const yBinNr = this.dataBinningY.determineBinNr(point[1], minY, maxY);
            if (yBinNr === false || yBinNr === null) {
                continue;
            }

            if (!binsGrid[yBinNr]) {
                throw new Error(`Unable to categorize point ${i}, unable to find row ${yBinNr} on bins grid`);
            }

            const xBinNr = this.dataBinningX.determineBinNr(point[0], minX, maxX);
            if (xBinNr === false || xBinNr === null) {
                continue;
            }

            if (!binsGrid[yBinNr][xBinNr]) {
                throw new Error(`Unable to categorize point ${i}, unable to find cell ${xBinNr} on bins grid row ${yBinNr}`);
            }

            binsGrid[yBinNr][xBinNr].add(point[0], point[1]);
        }

        return binsGrid;
    }

    createBin(binX, binY) {
        return new DataBin2D(binX, binY);
    }

    createGridOfBins(dataX, dataY) {
        const binsX = this.dataBinningX.determineBins(dataX);
        const binsY = this.dataBinningY.determineBins(dataY);

        const nrOfBinsX = binsX.length;
        const nrOfBinsY = binsY.length;

        const bins = [];

        // TODO - consider using one dimensional version
        for (let y = 0; y < nrOfBinsY; y++) {
            const binsRow = [];
            const binY = binsY[y];
            for (let x = 0; x < nrOfBinsX; x++) {
                const binX = binsX[x];
                binsRow.push(
                    this.createBin(binX, binY)
                );
            }

            bins.push(binsRow);
        }

        return bins;
    }
}

export default DataBinning2D;